import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, ArrowLeft } from 'lucide-react';
import { Button } from '../components/Button';
import { ProtectedLayout } from '../components/ProtectedLayout';

export function JournalingOverview() {
  const navigate = useNavigate();

  return (
     <ProtectedLayout>
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <BookOpen className="w-8 h-8 text-[#236caf]" />
              <h1 className="text-3xl font-bold text-[#004060]">Core Victory Method — Why It Works</h1>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Button>
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-600 mb-6">
              The Core Victory Method is a simple daily planning practice designed to help you get ahead by staying focused on what matters most, celebrating progress, and taking consistent action.
            </p>

            <p className="text-gray-600 mb-6">
              Every day, you answer five questions that guide you to reflect, reset, and move forward. These questions are based on research-backed principles from psychology, behavior change, and performance science.
            </p>

            <div className="space-y-8 my-12">
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-[#004060] mb-3">Question 1: What can you celebrate about yesterday?</h3>
                <p className="text-gray-600">
                  Helps train your brain to look for progress. Research shows that celebrating even small wins releases dopamine, the brain's motivation chemical. This not only feels good, but it increases your energy and momentum for the next step. (Teresa Amabile, The Progress Principle)
                </p>
              </div>

              <div className="bg-green-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-[#004060] mb-3">Question 2: What is your goal or most pressing problem today?</h3>
                <p className="text-gray-600">
                  Helps bring clarity and focus. Writing down your goal activates your brain's selective attention system, helping you notice opportunities and filter out distractions. (Dr. Gail Matthews, Goal-Setting Study)
                </p>
              </div>

              <div className="bg-purple-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-[#004060] mb-3">Question 3: What is and isn't working?</h3>
                <p className="text-gray-600">
                  Invites you to reflect honestly. This builds self-awareness and creates positive momentum by noticing progress while identifying challenges. Seeing even small signs of progress increases engagement, motivation, and creative problem-solving. (BJ Fogg, Tiny Habits; Positive Psychology Research)
                </p>
              </div>

              <div className="bg-orange-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-[#004060] mb-3">Question 4: What do your core values say to do?</h3>
                <p className="text-gray-600">
                  Connects your actions to who you are at your best. Research shows that reflecting on your core values increases resilience, willpower, and long-term motivation, even when things are difficult. (Dr. Geoffrey Cohen, Stanford University)
                </p>
              </div>

              <div className="bg-teal-50 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-[#004060] mb-3">Question 5: What action will you take today?</h3>
                <p className="text-gray-600">
                  Turns reflection into movement. Writing down your next step increases the likelihood of follow-through by up to 42%. Repeating this step daily builds habits, identity, and results over time. (Dr. Gail Matthews; James Clear, Atomic Habits)
                </p>
              </div>
            </div>

            <p className="text-gray-600 mb-6">
              Repeating this simple practice every day rewires your brain to look for wins, focus on what matters, stay aligned with your values, and take action — which is how real progress happens.
            </p>

            <h2 className="text-2xl font-semibold text-[#004060] my-8">The Power of Flexibility</h2>

            <p className="text-gray-600 mb-6">
              One of the most powerful aspects of the Core Victory Method is its flexibility. You can apply it to any area of your life where you want to grow or overcome a challenge. People use it for their business goals, health and fitness, relationships, parenting, leadership, creativity, and personal development. Some start with one specific area — like building their business — but quickly realize that the skills they develop through the CVM carry over into other parts of their life.
            </p>

            <p className="text-gray-600 mb-6">
              That's because clarity, celebration, values-aligned action, and momentum are transferable skills. As you practice solving problems and taking action in one area, you strengthen your ability to do the same in any other area. Business owners find themselves communicating better with their teams. Parents find themselves showing up more intentionally with their kids. Leaders find themselves staying grounded in tough situations.
            </p>

            <p className="text-gray-600 mb-6">
              The more you use the Core Victory Method, the more capable and confident you become — not just in achieving your current goal, but in facing whatever comes next.
            </p>

            <div className="bg-gradient-to-r from-[#004060] to-[#236caf] text-white p-8 rounded-lg mt-12">
              <p className="text-xl font-medium mb-0">
                This is how you get ahead every day — not by doing more, but by doing what matters most, on purpose, with consistency.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
        </ProtectedLayout>
  );
}